// File: MobileDragDropService.js

//==================================================
//
// A service that carries out the details of drag /
// drop. Exports singleton.
//
// If the target is a normal control, delegate
// the drop to the page (canvas). If the target is 
// a container, pass the drop to it (groups or tables)
//
// Lou Mauget, November 19-Dec 11 2015
//
//==================================================
import { Model } from 'backbone';
import $ from 'jquery';
import _ from 'lodash';
import dragGhostFragment from 'drag-ghost.html';
import util from 'util/Util.js';
import listService from 'ListService.js';
import sequenceService from 'SequenceService.js';
import factoryService from 'FactoryService.js';
import constants from 'Constants.js';
import geometryService from 'GeometryService.js';
import focusController from 'FocusController.js';
import zoomService from 'ZoomService.js';
import eventBus from 'EventBus.js';
import undoService from 'UndoService.js'
import Immutable from 'immutable';
import crudController from 'CrudController';
import textConstants from 'TextConstants';
import AlertDialogView from 'common-views/AlertDialogView.js';
import userService from 'UserService.js';
import thumbPageListUtil from 'util/ThumbPageListUtil.js';
import utilMobile from './../mobile/commons/MobileGeneralFunctions';
import mobileGroupDomHandler from './../views/mobile/containers/MobileGroupDomHandler';
import notificationService from "../service/NotificationService";
import groupService from './GroupService';
import themeService     from 'ThemeService';

const SILENT = constants.MODEL_SILENT;
const namespaceDragDropService = '.nameSpaceDragDropService';

class DragDropService {

    constructor() {
        // We drag-drop onto the invisible overlay floating above the grid canvas.
        // That's so that we can draw a muti-selection rectangle on it without disturbing the grid
        // or the controls floating above both canvases.
        this.canvasId = constants.PAGESEL_ID;

        this.groupKeys = Immutable.Set(['control_group', 'control_radio_button_group', 'control_checkbox_group']);
        this.formControls = null;
    }

    // Differentiate events: prevent collisions with handler on/off for given event in other modules.
    static get namespace() {
        return namespaceDragDropService;
    }

    static get namespaceBehaviors() {
        return '.nameSpaceDragDropServiceBehaviors';
    }

    static get namespaceWorkflow() {
        return '.nameSpaceDragDropServiceWorkflow';
    }

    //==========================================================================================
    // Used by creation and move.
    //
    // Drag ghost* is a transparent wireframe having dotted border, sized to that of dragged
    // control.
    //
    // *Also known as "drag image" in HTML5-speak.
    //
    // if controls are multi-selected, the drag ghost is a group of wireframes sized and
    // positioned relative to one-another.
    //
    // Note: this cannot be done in vanilla IE9. It has a hard-coded drag image.
    //==========================================================================================
    createRawDragGhost(height = 0, width = 0, inputWithLabel) {

        $(constants.GHOST_CNR_ID).empty();
        const $ghost = $(dragGhostFragment).appendTo(constants.GHOST_CNR_ID);
        const result = { $ghost: $ghost, nwPoint: undefined };

        if (listService.selectedCount > 1) {
            // Get selections sorted asc by element position -- model.attributes (left, top) values
            const selected = listService.sortSelected();

            // Sorted selection's first point is Northwestern-most origin point.
            // Subtract this point from all grouped control origins
            const nwPoint = {
                left: selected.first().model.attributes.left,
                top: selected.first().model.attributes.top
            };

            // Generate asc unique sequence for this drag-image.
            let seqNum = 1;

            // Assemble the wireframes into a single drag ghost relocated to zero origin
            selected.forEach((view, domId) => {

                const relocatedOrigin = {
                    left: view.model.attributes.left - nwPoint.left,
                    top: view.model.attributes.top - nwPoint.top
                };

                const $clone = $(constants.ID_SEL + domId)
                    .clone()
                    .prop('id', `ghost${seqNum++}`)
                    .css({
                        'background-color': constants.CLR_INVISIBLE, 'color': constants.CLR_INVISIBLE,
                        'position': 'absolute', 'border': constants.DRAG_BORDER
                    })
                    .css(relocatedOrigin)
                    .attr('draggable', false)
                    .removeClass('has-menu pf-control')
                    .empty();

                // Add clone to drag-image -- the ghost
                $ghost.append($clone);
            });

            result.$ghost = $ghost;
            result.nwPoint = nwPoint;
        }
        else {

            if(inputWithLabel){
                //label ghost
                const label = $("<div id='ghostLBL'>").css(
                    {
                        left: 0,
                        top: 0,
                        height: "18px",
                        width: "50px",
                        'background-color': constants.CLR_INVISIBLE, 'color': constants.CLR_INVISIBLE,
                        'position': 'absolute', 'border': constants.DRAG_BORDER
                    });

                $ghost.append(label);

                //input ghost
                const input = $("<div id='ghostINPUT'>").css(
                    {
                        left: 0,
                        top: 20,
                        height: height,
                        width: width,
                        'background-color': constants.CLR_INVISIBLE, 'color': constants.CLR_INVISIBLE,
                        'position': 'absolute', 'border': constants.DRAG_BORDER
                    });
                $ghost.append(input);
                result.$ghost = $ghost;

            }
            else {
                // Single-drag: used for creation or single-control movement
                $ghost.height(height);
                $ghost.width(width);
            }

            result.nwPoint = { left: 0, top: 0 };
        }
        return result;
    }


    //==========================================================================================
    // Property true iff elemRect entirely contained within current PF page rectangle.
    //==========================================================================================
    isEntirelyContained(elemRect) {
        const parentRect = _.clone(this.pageRect);
        parentRect.top -= 2;
        parentRect.height--;

        return geometryService.rect1ContainsPartiallyRect2(parentRect, elemRect);
    }

    //==========================================================================================
    // Modifying drop position if half of the element is dropped on the edge of the form
    //==========================================================================================
    modifyControlOnEdgeDrop({ left, top }, cdata) {
        let dropPosTemp = {};
        const parentRect = _.clone(this.pageRect);

        // Right Edge
        if ((left + util.extractNum(cdata.width)) > (parentRect.width)) {
            let newPosition = left - (util.extractNum(cdata.width));
            let spaceBetweenControlAndForm = parentRect.width - (newPosition + util.extractNum(cdata.width));
            dropPosTemp.left = newPosition + spaceBetweenControlAndForm;

        }

        // Left Edge
        else if (left < 0 &&
            ((left + util.extractNum(cdata.width)) < (parentRect.width))) {
            dropPosTemp.left = 0;
        }
        else {
            dropPosTemp.left = left;
        }

        // Bottom Edge
        if ((top + util.extractNum(cdata.height)) > (parentRect.height)) {
            let newPosition = top - (util.extractNum(cdata.height));
            let spaceBetweenControlAndForm = parentRect.height - (newPosition + util.extractNum(cdata.height));
            dropPosTemp.top = newPosition + spaceBetweenControlAndForm;
        }

        // Top Edge
        else if (top < 0 &&
            ((top + util.extractNum(cdata.height)) < (parentRect.height))) {
            dropPosTemp.top = 0;
        }

        else {
            dropPosTemp.top = top;
        }
        return dropPosTemp;

    }


    //==========================================================================================
    // Property: Perfect Forms current page position within DOM window
    //==========================================================================================
    //get pagePos() {
    //    const offset = $(this.canvasId).offset();
    //    return {
    //        top: offset.top - $(window).scrollTop(),
    //        left: offset.left - $(window).scrollLeft()
    //    };
    //}

    //==========================================================================================
    // Property: Perfect Forms current page width and height
    //==========================================================================================
    get pageDim() {
        return {
            width: $(this.canvasId).width(),
            height: $(this.canvasId).height()
        };
    }


    //==========================================================================================
    // Property: Perfect Forms current page rectangle in offset coordinates compensated for
    // window scroll position.
    //==========================================================================================
    get pageRect() {
        return {
            top: util.pagePos.top,
            left: util.pagePos.left,
            height: this.pageDim.height,
            width: this.pageDim.width
        };
    }


    //==========================================================================================
    // Get rectangle of a DOM element, given its DOM ID.
    //==========================================================================================
    elementRect(elementId) {
        const
            $id = $(constants.ID_SEL + elementId),
            $window = $(window);

        return {
            left: $id.offset().left - $window.scrollLeft(),
            top: $id.offset().top - $window.scrollTop(),
            width: $id.width(),
            height: $id.height()
        };
    }

    droppedOnGroupObject(dropRect, groupId, parentGroup) {
        let groupView = null;
        let affectedControl = listService.getObject(groupId);
        const isControlGroup = () => affectedControl && affectedControl.model ?
            this.groupKeys.contains(affectedControl.model.get('factoryKey')) : parentGroup;

        if (!isControlGroup() && affectedControl && affectedControl.groupId) {
            affectedControl = listService.getObject(affectedControl.groupId);
        }

        if (isControlGroup()) {
            const groupRect = {
                top: util.extractNum(affectedControl.model.get('top')),
                left: util.extractNum(affectedControl.model.get('left')),
                width: util.extractNum(affectedControl.model.get('width')),
                height: util.extractNum(affectedControl.model.get('height'))
            };

            if (geometryService.rect1ContainsPartiallyRect2(groupRect, dropRect)) {
                // return group view
                groupView = affectedControl;

            }
        }

        if (groupView && groupView.id !== groupId) {
            groupView = this.droppedOnGroupObject(dropRect, groupView.id, groupView);
        }

        return groupView;
    }

    getControlDimensions(view) {
        return {
            width: util.extractNum(view.model.get('width')),
            height: util.extractNum(view.model.get('height')),
            top: view.model.get("top"),
            left: view.model.get("left")
        };
    }

    setNewDimensions(ctrlDimensions, ctrlView) {

        const $el = $(constants.ID_SEL + ctrlView.model.get("id"));

        const pfControl = $el.find(".pf-control-core:first");
        pfControl.css({
            "height": ctrlDimensions.height,
            "width": ctrlDimensions.width
        });

        if (util.extractNum(pfControl.css("min-height")) > 0) {
            pfControl.css({
                "min-height": ctrlDimensions.height
            });
        }

        if (util.extractNum(pfControl.css("line-height")) > 20) {
            pfControl.css({
                "line-height": ctrlDimensions.height + "px"
            });
        }

        ctrlView.model.set("height", ctrlDimensions.height, SILENT);
        ctrlView.model.set("width", ctrlDimensions.width, SILENT);
    }

    setNewPosition(ctrlView, ctrlDimensions) {
        const $el = $(constants.ID_SEL + ctrlView.model.get("id"));

        //PF-9523 there is an offset of 4px in tables because of the resizing handles, 
        //the offset should only be taken into account in the visual position but not the position in the model
        let tableOffset = 0;
        if (ctrlView.model.get("factoryKey") === "control_table") {
            tableOffset = 4;
        }

        $el.css({ 'top': ctrlDimensions.top - tableOffset, 'left': ctrlDimensions.left });
        ctrlView.model.set("top", ctrlDimensions.top, SILENT);
        ctrlView.model.set("left", ctrlDimensions.left, SILENT);
    }

    checkBounds(dropPos, group, control) {
        let value = dropPos;
        if (value > 0) {
            let diff = control.pos - group.pos;
            value = diff > 0 ? diff : 0;

            if (value + control.val > group.val) {
                value = group.val - control.val;
            }
        }
        return value;
    }

    moveCanvasToGroup(view, grpVw, dropPos) {

        sequenceService.tabIndexParentId = grpVw.id;
        const nextIndex = sequenceService.nextTabIndex();
        const groupFrom = view.groupId;

        view.groupId = grpVw.id;

        if (groupFrom) {
            let childList = listService.getObject(groupFrom).model.get('childList');
            const oldIndex = childList.findIndex(child => child.id === view.id);
            childList.splice(oldIndex, 1);
            listService.getObject(groupFrom).model.set('childList', childList, { silent: true });
        }

        view.model.set('tabIndex', nextIndex, { silent: true });
        sequenceService.tabIndexParentId = listService.currentPageId;

        const groupDimensions = groupService.getGroupDimensions(grpVw);
        let ctrlDimensions = this.getControlDimensions(view);

        //Checks the group bounds to ensure the control is dropped within the group        
        ctrlDimensions.left = this.checkBounds(dropPos.left, { "pos": groupDimensions.left, "val": groupDimensions.width }, { "pos": ctrlDimensions.left, "val": ctrlDimensions.width });
        ctrlDimensions.top = this.checkBounds(dropPos.top, { "pos": groupDimensions.top, "val": groupDimensions.height }, { "pos": ctrlDimensions.top, "val": ctrlDimensions.height });


        //Checks the control height and width and adjusts according to the group height and width
        if (ctrlDimensions.width > groupDimensions.width) {
            ctrlDimensions.width = groupDimensions.width;
            ctrlDimensions.left = 0;
        }

        if (ctrlDimensions.height > groupDimensions.height) {
            ctrlDimensions.height = groupDimensions.height;
            ctrlDimensions.top = 0;
        }

        //Assign the object to the group
        grpVw.model.get('childList').push(view.model);
        listService.reorderTabIndexes(view.id, ''); // reset Group
        sequenceService.tabIndexParentId = listService.currentPageId;
        listService.reorderTabIndexes('', '', listService.currentPageId);  // reset page
        thumbPageListUtil.moveElementToNewParent(view.id, grpVw.id, view.pageId);
        $(constants.ID_SEL + view.id).appendTo(`${constants.ID_SEL}${'groupItems' + grpVw.id}`);

        focusController.clearAllSelected();
        focusController.selectionRectangle( grpVw.id );

        //Adjust the control to the dom in relation to its new group
        this.setNewDimensions(ctrlDimensions, view);
        this.setNewPosition(view, ctrlDimensions);
    }

    moveGroupToCanvas(view) {

        crudController.removeChildFromGroupModel(view.groupId, view.id);

        const mobileObject = listService.getMobileObject(view.id);
        if (mobileObject) {
            mobileObject.isNew = true;
            mobileObject.groupId = undefined;
        }

        if (parseInt(listService.timesWizardCompleted) > 0) {
            eventBus.trigger(constants.EV_VALIDATE_PULSE_ICON_EVENT);
        }

        const grpId = view.groupId;
        view.groupId = undefined;

        // Logic - moving out of group, need to set object to Highest index
        sequenceService.tabIndexParentId = listService.currentPageId;
        const nextPageTabIndex = sequenceService.nextTabIndex();
        view.model.set('tabIndex', nextPageTabIndex, { silent: true });

        listService.reorderTabIndexes(view.id, '');  // reset page
        listService.reorderTabIndexes('', '', grpId); // reset Group
        sequenceService.tabIndexParentId = listService.currentPageId;

        //Update thumb page item objects tree
        thumbPageListUtil.moveElementToNewParent(view.id, null, view.pageId);
        $(constants.ID_SEL + view.id).appendTo(`${constants.ID_SEL}aPage`);

        const ctrlDimensions = this.getControlDimensions(view);
        this.setNewDimensions(ctrlDimensions, view);
        this.setNewPosition(view, ctrlDimensions);
    }

    moveCanvasToCanvas(view, dropPos) {

        this.setNewPosition(view, { "top": dropPos.top, "left": dropPos.left });
    }

    moveGroupToGroup(view, dropPos) {

        const grpVw = listService.getObject(view.groupId);
        this.moveCanvasToGroup(view, grpVw, dropPos);

    }

    //==========================================================================================
    // Implement dragging a control, or a multi-selection of controls, from one spot to
    // another on the page.
    // No action if any control's rectangle does not wholly fit within the page at the drop point.
    //==========================================================================================
    moveControl(ev, moveArray, keyboardMove = false) {
        ev.stopPropagation();

        console.log("move control", this);

        // Drop point relative to document object
        const p = this.align(ev.originalEvent.pageX, ev.originalEvent.pageY, keyboardMove);
        const dropX = p.x;
        const dropY = p.y;
        //const movedControls = [[], []];
        const movedControls = { previous: [], new: [] };
        if (moveArray) {
            let isAbortDrop = false;

            // For each selected control ..
            _.each(moveArray, data => {

                // Account for mouse offset within control or group
                const ptrOffsetY = data.ptrOffsetY;
                const ptrOffsetX = data.ptrOffsetX;

                // Compute actual relative drop position. Account for ptr offset, scroll offset
                const p = this.align(
                    dropX - util.pagePos.left - data.ptrOffsetX - $(window).scrollLeft(),
                    dropY - util.pagePos.top - data.ptrOffsetY - $(window).scrollTop(), keyboardMove);
                const dropPos = {
                    left: p.x,
                    top: p.y
                };

                // Encapsulated computation of moved control rect: used for confining to PF page.
                // Closes over dropX, dropY, ptrOffsetX, ptrOffsetXY
                const movedRect = (drpY, drpX) => {

                    const $dropSource = $(constants.ID_SEL + data.sourceId);
                    const dim = { height: $dropSource.height(), width: $dropSource.width() };

                    return {
                        top: drpY - ptrOffsetY - $(window).scrollTop(),
                        left: drpX - ptrOffsetX - $(window).scrollLeft(),
                        height: dim.height,
                        width: dim.width
                    };
                };

                // No drop: we take two passes. This tests that entire multi-move fits on page
                if (!this.isEntirelyContained(movedRect(dropY, dropX))) {
                    isAbortDrop = true;
                    console.log('Refused this drop');
                }
            });

            // Okay, all controls would fit in the parent page. Otherwise, no drop.
            if (!isAbortDrop) {

                // For each control ..
                _.each(moveArray, data => {

                    // Visually carry out drop by changing the CSS top and bottom properties
                    const $el = $(constants.ID_SEL + data.sourceId);

                    // Record the drop by updating the model
                    const id = $el.attr('id');
                    const view = listService.getObject(id);


                    let snapToNeighbor;
                    // Okay. Good to go ... create new control here
                    snapToNeighbor = this.checkSnapToNeighbor(ev, data, view);

                    const p = this.align(
                        dropX - util.pagePos.left - data.ptrOffsetX - $(window).scrollLeft(),
                        dropY - util.pagePos.top - data.ptrOffsetY - $(window).scrollTop(), keyboardMove);

                    let dropPos;
                    if (snapToNeighbor.left !== 0) {
                        dropPos = {
                            left: snapToNeighbor.left,
                            top: snapToNeighbor.top
                        };
                    }
                    else {
                        dropPos = {
                            left: p.x,
                            top: p.y
                        }
                    }

                    dropPos = this.modifyControlOnEdgeDrop(dropPos, data);

                    // Cleaning up any OutOfBounds CSS Class that may exist in the dropped control.
                    $el.removeClass(constants.OUT_OF_BOUNDS_CLASS);
                    $el.css('top', dropPos.top);
                    $el.css('left', dropPos.left);

                    // capture the Before values for the Undo in the calling method.
                    movedControls.previous.push({
                        id: id,
                        left: view.model.get('left'),
                        top: view.model.get('top'),
                        width: view.model.get('width'),
                        height: view.model.get('height'),
                        groupId: view.groupId
                    });

                    //we make sure moving items inside of groups works fine

                    view.model.set({
                        left: dropPos.left,
                        top: dropPos.top,
                        undoAction: true
                    }, SILENT);

                    let grpVw;
                    let isObjectInsideGroup;

                    if (ev.currentTarget && data.ptrOffsetX) {
                        const dropGrpRect = {
                            top: dropPos.top,
                            left: dropPos.left,
                            width: util.extractNum(view.model.get('width')),
                            height: util.extractNum(view.model.get('height')),
                            id: view.model.get('id')
                        };

                        let droppedOn;

                        if(ev.currentTarget.id !== view.id){
                            const $view = $(constants.ID_SEL + view.id);
                            const isChild = $view.find(constants.ID_SEL + ev.currentTarget.id);

                            droppedOn = isChild.length ?  view.groupId : ev.currentTarget.id;
                        }
                        else{
                            droppedOn = view.groupId;
                        }

                        grpVw = this.droppedOnGroupObject(dropGrpRect, droppedOn);
                        isObjectInsideGroup = grpVw && view.groupId === grpVw.id;
                    }

                    if (!isObjectInsideGroup) {
                        if (ev.currentTarget && ev.offsetX) {
                            if (grpVw) {//If element is dropped inside a group
                                this.moveCanvasToGroup(view, grpVw, dropPos, $el, data);
                            }
                            else {//If element is inside of a group and it was dropped over canvas
                                if (view.groupId) {
                                    this.moveGroupToCanvas(view);
                                }
                                else {
                                    this.moveCanvasToCanvas(view, dropPos);
                                }
                            }
                        }
                    }
                    else {//If element is inside of a group and it was dropped inside the same group
                        if (view.groupId) {
                            this.moveGroupToGroup(view, dropPos);
                        }
                    }

                    movedControls.new.push({
                        id: id,
                        left: view.model.get('left'),
                        top: view.model.get('top'),
                        width: view.model.get('width'),
                        height: view.model.get('height'),
                        groupId: view.groupId
                    });

                    //movedControls[1].push({ id: id, left: view.model.get('left'), top: view.model.get('top'), width: view.model.get('width'), groupId: view.groupId });

                    // Check if zoom
                    if (listService.currentForm.model.attributes.zoomScale) {
                        zoomService.handleSpecificControlZoom(listService.currentForm.model.attributes.zoomScale, id);
                        // Place the real position where the control is placed when it's dropped inside the canvas,
                        // but it is in zoomed mode.
                        view.model.set({
                            top: view.model.attributes.top / listService.currentForm.model.attributes.zoomScale,
                            left: view.model.attributes.left / listService.currentForm.model.attributes.zoomScale
                        }, { silent: true });
                    }
                });
            }
        }

        return movedControls;
    }

    isFeatureEnabledForControl(factoryKey, hideAlert) {
        let enabled = false;
        if (factoryKey === constants.CONTROL_TYPE_WEBSIGNATURE) {
            const features = userService.getUserFeatures();
            enabled = features.WebSignature;
            if (!enabled && !hideAlert) {
                //Setting up the modal
                const confirmView = new AlertDialogView({
                    model: new Model({
                        text: textConstants.WEB_SIGNATURE_FEATURE_NOT_ENABLED_TEXT,
                        title: textConstants.WEB_SIGNATURE_FEATURE_NOT_ENABLED_TITLE,
                        height: 200
                    })
                });

                confirmView.render();
            }
        } else {
            enabled = true;
        }

        return enabled;
    }

    getOffsetParents(target) {
        let offSet = { left: 0, top: 0 };
        if (target && target.id !== "aPage") {
            offSet = this.getOffsetParents(target.offsetParent);
            offSet.left += target.offsetLeft || 0;
            offSet.top += target.offsetTop || 0;
        }
        return offSet;
    }

    calculateControlDropPosition(ev, cdata, factoryParams) {

        const snapToNeighbor = this.checkSnapToNeighbor(ev, cdata, factoryParams);

        // const scrollLeft = $(window).scrollLeft();
        // const scrollTop = $(window).scrollTop();

        let dropPos, position, cursorOffset = { left: 0, top: 0 };

        const getOffsetParents = this.getOffsetParents(ev.currentTarget);
        const targetOffset = this.isTargetGroupOrPage(ev.currentTarget) ? { left: 0, top: 0 } : { left: ev.currentTarget.offsetLeft, top: ev.currentTarget.offsetTop };

        console.log("LEFT ", util.pagePos.left, "TOP", util.pagePos.top);
        let data;

        if (cdata.position) { //It comes from the search tools
            cursorOffset.left = ev.originalEvent.offsetX;
            cursorOffset.top = ev.originalEvent.offsetY;
            data = { cx: cursorOffset.left, cy: cursorOffset.top };

        } else {
            cursorOffset.left = cdata.cx;
            cursorOffset.top = cdata.cy;
            data = cdata;
        }

        position = this.align(
            ev.originalEvent.pageX - util.pagePos.left - cursorOffset.left - getOffsetParents.left + targetOffset.left,
            ev.originalEvent.pageY - util.pagePos.top - cursorOffset.top - getOffsetParents.top + targetOffset.top);

        //If any of the coordenates is less than zero the value should be then zero.
        dropPos = { top: position.y, left: position.x };

        const group = groupService.findParentGroup(ev.currentTarget);

        if (group) {
            dropPos = groupService.checkGroupBounds(group, data, dropPos);
        }

        if (snapToNeighbor.left !== 0) {
            dropPos = {
                left: snapToNeighbor.left,
                top: snapToNeighbor.top
            };
        }

        dropPos = this.modifyControlOnEdgeDrop(dropPos, cdata);

        return dropPos;
    }

    isTargetGroupOrPage(ev) {
        const targetControl = listService.getObject(ev.id);
        let targetContainer = true;
        if (targetControl) {
            const isGroup = groupService.isGroup(targetControl.model.get('factoryKey'));
            targetContainer = isGroup || targetControl.model.get('id') === "aPage";
        }
        return targetContainer;
    }

    getMinDimensions(control) {
        const model = control.model; 

        const myFont = themeService.resolveThemeFont('fontFamily', model.get('fontFamily')) + ',' +
            themeService.resolveThemeFont('fontSize', model.get('fontSize')) + 'px,' +
            themeService.resolveThemeFont('textWeight', model.get('textWeight')) + ',' +
            themeService.resolveThemeFont('textStyle', model.get('textStyle'));

        const myText = model.get('text') + '.'; // add space to avoid wrapping if too close.
        const currentWidth = util.extractNum(model.get('width'));
        const currentHeight = util.extractNum(model.get('height'));

        //Add here additional styling if any controls require it
        const additionalCss = model.get('factoryKey') === 'control_mask_text_input' ? { 'letter-spacing': '0.5em' } : {};
                
        const myWidth = Math.round(util.measureText(myText, myFont, additionalCss)) + 10; //Add extra px to avoid wrapping
        if(currentWidth < myWidth){
            model.set('width', myWidth, SILENT);
        }
        
        const myHeight = Math.round(util.measureHeight(myText, myFont)) + 1; //Add extra px to ensure height for letters like 'p', 'y' and 'q'
        if(currentHeight < myHeight){
            model.set('height', myHeight, SILENT);
        }
    }

    //==========================================================================================
    // Create a control on the layout spot, from a dropped factory selection ghost image.
    // No action if control's rectangle does not wholly fit within the layout at the drop point.
    //==========================================================================================
    createcontrol(ev, data) {
        if (data) {

            const cdata = data[0];
            const factoryKey = cdata.iconId;

            if (this.isFeatureEnabledForControl(factoryKey)) {

                const factoryParams = factoryService.factories[factoryKey];
                sequenceService.tabIndexParentId = listService.currentPageId;
                // The factory knows to create properties for the model

                if (sequenceService.getControlNumber() < 0) {
                    sequenceService.initControlNumber();
                }
                sequenceService.addControlNumber();

                // const parentGroup = this.findParentContainer(ev.currentTarget);
                const isGroup = groupService.isGroup(factoryKey);

                let dropPos = this.calculateControlDropPosition(ev, cdata, factoryParams);
                let label;
                
                if(factoryParams.hasLabel){
                   const factoryLabel =  factoryService.factories["control_text"];
                   label = factoryLabel.factory.createView("control_text", dropPos);
                   this.getMinDimensions(label);
                   dropPos.top += parseInt(label.model.get("height")) + 2;
                   groupService.droppedOnGroupObject(ev, label, dropPos);

                   listService.addObject(label.id, label);
                   sequenceService.addControlNumber();
                }
                
                let control = !isGroup ? factoryParams.factory.createView(factoryKey, dropPos) : groupService.createNewGroup(factoryKey, dropPos);

                if(control.model.get('factoryKey') !== "control_vertical_separator" && control.model.get('factoryKey') !== "control_horizontal_separator"){
                    this.getMinDimensions(control);
                }
               
                groupService.droppedOnGroupObject(ev, control, dropPos);

                control.validateToStayInBounds(control.model, true);

                listService.addObject(control.id, control);

                // if we dropped a pre-configured Group with Children
                if (control.model.get('factoryKey').indexOf('group') > -1) {
                    let childList = control.model.get('childList');
                    if (childList && childList.length) {
                        _.forEach(childList, child => {
                            const _fp = factoryService.factories[child.get('factoryKey')];
                            const vw = _fp.factory.createView(child.get('factoryKey'), child.get('pos'), true);
                            child._events = vw.model._events;
                            vw.groupId = control.id;
                            vw.model = child;
                            vw.id = child.get('id');
                            listService.addObject(vw.id, vw);
                        });
                    }
                }
                
                if (control.model.get('invalid') === false) {

                    // Determine if we dropped inside of a Group object (i.e. Group, Checkbox Group, Radio Button Group)
                    // If so, add to group's children as it would have been done via the Group Method from Menu
                    //const dropGrpRect = {top: util.extractNum(control.model.get('top')), left: util.extractNum(control.model.get('left')), width: util.extractNum(control.model.get('width')), height: util.extractNum(control.model.get('height')) };
                    // Only need the drop point to be in the group
                    //groupService.droppedOnGroupObject(ev, control, dropPos);

                    //PF-2486 - Form Design - Layout - Cannot Drag Asset onto canvas
                    if (cdata.assetId) {
                        control.model.set('assetId', cdata.assetId, SILENT);
                        control.model.trigger('change:assetId');
                    }

                    listService.addObject(control.id, control);
                    //groupService.addControlToGroup(parentGroup, control);
                    control.render();
                    // groupService.checkGroupBorders(control, grpVw);

                    if(label){
                        listService.addObject(label.id, label);
                        label.render();
                        label.input = control;

                        control = label; //change focus to the label
                    }

                    // Check if zoom
                    if (listService.currentForm.model.attributes.zoomScale) {
                        zoomService.handleSpecificControlZoom(listService.currentForm.model.attributes.zoomScale, control.id);
                        // Placing the real position where the control is placed when its dropped inside the canvas and it is on zoomed mode.
                        control.model.attributes.top = control.model.attributes.top / listService.currentForm.model.attributes.zoomScale;
                        control.model.attributes.left = control.model.attributes.left / listService.currentForm.model.attributes.zoomScale;
                    }


                    listService.currentId = control.id;

                    // focusController.clearAllSelected();

                    // PF-1111 clear sections, then select this one
                    focusController.selectionRectangle(control.id);

                    listService.addToSelected(control.id);
                    let currentSize = listService.selectedCount;
                    util.validateMenuItems(currentSize);

                    const obj = listService.getObject(control.id);
                    const oName = obj.model.get("name");
                    $('.rail-header-text').text(oName + constants.RIGHT_PANEL_PROPERTIES_TITLE);
                    $('.rail-header-text').prop('title', oName + constants.RIGHT_PANEL_PROPERTIES_TITLE);
                    eventBus.trigger(constants.EV_UPDATE_BREADCRUMB, 'OBJECT', control.id);

                    // Paint the properties
                    focusController.switchPropsViews();
                    // Focus on default property tab
                    $('#settings-tab a').trigger("click");                    
                    
                    // Focus the control: turns on the blue border
                    window.setTimeout(() => {
                        $(constants.ID_SEL + control.id).trigger(constants.EV_FOCUS);
                        util.updateHelpDocumentationLink(control.model.get('factoryKey'))
                    }, 0);

                    //PF-10255 If editable controls enable inline editing after drop
                    const $el = $("#" + control.id);
                    if ($el.hasClass("pf-editable") && control.input) {
                        control._editTextInline({ currentTarget: $el[0], isLabel: factoryParams.hasLabel, control: control.input.id}, true);
                    }
                    else{
                        window.setTimeout(() => {
                            $(".control-name").trigger("select");
                        }, 10);
                    }

                    // Note: listenForInstanceDragStart() called by pageView
                    this.listenForDrop(constants.ID_SEL + control.id);

                    return control;
                }
            }
        }
    }


    //==========================================================================================
    // Create a control on the Behaviors canvas spot of a dropped factory selection copy image.
    //==========================================================================================
    createControlBehaviors(ev, data) {

        // Encapsulated computation of moved rect used for clipping to PF page
        // Closes over dropX, dropY, ptrOffsetX, ptrOffsetXY
        const droppedRect = (xferDat, drpY, drpX) => {
            const dropPosRelative = {
                top: drpY - xferDat.cy - $(window).scrollTop(),
                left: drpX - xferDat.cx - $(window).scrollLeft()
            };
            return {
                top: dropPosRelative.top,
                left: dropPosRelative.left,
                height: xferDat.height,
                width: xferDat.width
            };
        };

        // Drop point relative to document object
        const dropX = ev.originalEvent.pageX;
        const dropY = ev.originalEvent.pageY;

        const cdata = data[0];
        const factoryKey = cdata.iconId;
        const factoryParams = factoryService.behaviorsFactories[factoryKey];

        const p = this.align(
            dropX - util.pagePos.left - cdata.cx - $(window).scrollLeft(),
            dropY - util.pagePos.top - cdata.cy - $(window).scrollTop());
        const dropPos = {
            left: p.x,
            top: p.y
        };

        // console.log( `createControlBehaviors; (${dropPos.left}, ${dropPos.top})` );
        const control = factoryParams.factory.createView(factoryKey, dropPos);
        control.model.set('selected', true, { silent: true });
        undoService.add({
            undo() {
                listService.deleteBehaviorObject(control.id);
                control.unrender();
                listService.fcanvas.renderAll();
            },
            redo() {
                listService.addBehaviorObject(control.id, control);
                control.render();
                listService.fcanvas.renderAll();
            }
        }, textConstants.UNDO_NEW);

        listService.addBehaviorObject(control.id, control);
        
        // de-select all
        listService.fcanvas.trigger('mouse:down').trigger('mouse:up').deactivateAll().renderAll();

        control.render();

        listService.currentBehaviorId = control.model.get('id');

        util.updateHelpDocumentationLink(control.model.get('factoryKey'))
        eventBus.trigger(constants.EV_UPDATE_BEHAVIORS_ONLY);

    }

    getObjectCount(control) {
        const
            gridData = control.model.get('gridData'),
            nonEmptyObjects = gridData.filter(function (data) { return data.name !== "" });
        return nonEmptyObjects.length.toString();
    }

    //==========================================================================================
    // Move a control on the Workflow canvas.
    //==========================================================================================
    moveControlWorkflow(ev, data) {
        // console.log( 'moveControlBehaviors' );
    }

    //==========================================================================================
    // Create a control on the Workflow canvas spot of a dropped factory selection copy image.
    //==========================================================================================
    addWorkFlowToListService(control) {
        listService.addWorkflowObject(control);
    }

    createControlWorkflow(ev, data) {

        // Encapsulated computation of moved rect used for clipping to PF page
        // Closes over dropX, dropY, ptrOffsetX, ptrOffsetXY
        const droppedRect = (xferDat, drpY, drpX) => {
            const dropPosRelative = {
                top: drpY - xferDat.cy - $(window).scrollTop(),
                left: drpX - xferDat.cx - $(window).scrollLeft()
            };
            return {
                top: dropPosRelative.top,
                left: dropPosRelative.left,
                height: xferDat.height,
                width: xferDat.width
            };
        };

        // Drop point relative to document object
        const dropX = ev.originalEvent.pageX;
        const dropY = ev.originalEvent.pageY;

        const cdata = data[0];
        const factoryKey = cdata.iconId;
        const factoryParams = factoryService.workflowFactories[factoryKey];

        const p = this.align(
            dropX - util.pagePos.left - cdata.cx - $(window).scrollLeft(),
            dropY - util.pagePos.top - cdata.cy - $(window).scrollTop());
        const dropPos = {
            left: p.x,
            top: p.y
        };

        const control = factoryParams.factory.createWorkflowView(factoryKey, dropPos);
        control.model.set('selected', true, { silent: true });
        undoService.add({
            undo() {
                listService.deleteWorkflowObject(control);
                control.unrender();
                listService.fcanvas.renderAll();
            },
            redo() {
                listService.addWorkflowObject(control);
                control.render();
                listService.fcanvas.renderAll();
            }
        }, textConstants.UNDO_NEW);

        // de-select all
        listService.fcanvas.trigger('mouse:down').trigger('mouse:up').deactivateAll().renderAll();

        listService.addWorkflowObject(control);
        control.render();

        eventBus.trigger(constants.EV_ROLE_OR_STAGE_UPDATED);
        eventBus.trigger(constants.EV_REFRESH_REMOTE_DATA_FILTER);

        util.updateHelpDocumentationLink(control.model.get('factoryKey'));
    }

    //==========================================================================================
    // Create drag transfer data for moving one to N multi-selected controls.
    //==========================================================================================
    createDragData(ev) {

        const dragData = [];

        listService.selected.forEach((view, domId) => {

            const $el = $(util.jqId(domId));
            const offset = $el.offset();

            // Account for mouse offset within control. (pageX, pageY) is mouse pointer position.
            const p = this.align(ev.originalEvent.pageX - offset.left, ev.originalEvent.pageY - offset.top);
            const ptrOffsetX = p.x;
            const ptrOffsetY = p.y;

            const obj = {
                sourceId: domId,

                width: view.model.attributes.width,
                height: view.model.attributes.height,

                ptrOffsetY: ptrOffsetY,
                ptrOffsetX: ptrOffsetX
            };
            dragData.push(obj);
        });

        return dragData;
    }

    createDragDataIE(ev) {

        const dragData = [];

        listService.selected.map((view, domId) => {

            const $el = $(util.jqId(domId));
            const offset = $el.offset();

            const obj = {
                sourceId: domId,

                width: view.model.attributes.width,
                height: view.model.attributes.height,

                ptrOffsetY: offset.top,
                ptrOffsetX: offset.left
            };
            dragData.push(obj);
        });

        return dragData;
    }

    updateDragDataIE(dragDataIE, mainOffset, size) {
        const dragData = [];

        dragDataIE.forEach(function (valor, indice) {

            const obj = {
                sourceId: valor.sourceId,

                width: valor.width,
                height: valor.height,

                ptrOffsetX: ((mainOffset.left - valor.ptrOffsetX) + size.width),
                ptrOffsetY: ((mainOffset.top - valor.ptrOffsetY) + size.height)
            };
            dragData.push(obj);
        });

        return dragData;
    }

    //==============================================================================
    //  Returns align (x, y) as a point object
    //==============================================================================
    align(x, y, skip = false) {
        const form = listService.currentForm;
        const p = { x: x, y: y };

        if ((listService.snapToGrid || listService.snapToNeighbor) && form && !skip) {
            const gridWidth = util.extractNum(form.model.get(constants.PROP_GRID_WIDTH));
            const gridHeight = util.extractNum(form.model.get(constants.PROP_GRID_HEIGHT));

            if (gridWidth && gridHeight) {
                p.x = Math.round(x / gridWidth) * gridWidth;
                p.y = Math.round(y / gridHeight) * gridHeight;
            }
        }

        return p;
    }


    //==========================================================================================
    // Call when event should not trigger additional listeners
    //==========================================================================================
    _stopPropagation(ev) {
        ev.stopImmediatePropagation();
        ev.stopPropagation();
    }


    //==========================================================================================
    // Invoke this once. LAYOUT_ID delegate must exist, but no controls need be present yet.
    // DELEGATES to #layout-frame.
    //==========================================================================================
    listenForInstanceDragStart() {
        let currentCopiedText = "";
        $(constants.LAYOUT_ID).off(constants.EV_DRAG_START, constants.INSTANCE_CLASS);

        $(constants.LAYOUT_ID).on(constants.EV_DRAG_START, constants.INSTANCE_CLASS, ev => {


            // PF-1569 - prevent direct group child drag from dragging parent group
            this._stopPropagation(ev);

            const $el = util.jqId(ev.currentTarget.id);

            if (!listService.isSelected(ev.currentTarget.id)) {

                // Must not have clicked in  multi-select group if clicked control not selected.
                listService.clearSelected();
                focusController.clearAllSelected();

                // We depend upon dragging the selection ... so select clicked control in case it's not selected
                listService.addToSelected(ev.currentTarget.id);
                focusController.selectionRectangle(ev.currentTarget.id);
            }
            // Drop handler uses dragData to reconstitute the control.
            let dragData = this.createDragData(ev);
            let dragDataIE = this.createDragDataIE(ev);
            const dragImage = this.createRawDragGhost($el.height(), $el.width());
            let cx = $el.height() / 2;
            let cy = $el.width() / 2;
            // Account for mouse offset within control or control group
            // First control is the northwest control (we sorted by postion during createRaDragGhost)
            const nwOffset = util.jqId(listService.selected.first().model.attributes.id).offset();
            const offsetIE = util.jqId(ev.currentTarget.id).offset();
            // Use NW control's JQ offset value to adjust the mouse pointer within the drag image only.
            // THe eventual drop will use the real mouse position OK.
            const p = this.align(ev.originalEvent.pageX - nwOffset.left, ev.originalEvent.pageY - nwOffset.top);
            const ptrOffsetX = p.x;
            const ptrOffsetY = p.y;
            // If dataTransfer.setDragImage is not implemented, then is a browser without native support for custom drag image, mostly Internet Explorer
            if (ev.originalEvent.dataTransfer.setDragImage) {
                ev.originalEvent.dataTransfer.setDragImage(dragImage.$ghost.get(0), ptrOffsetX * util.getBrowserZoom(), ptrOffsetY * util.getBrowserZoom());
            } 

            // Stateful hack for IE9 that also satisfies higher browsers
            this.dropEffect = constants.DROP_MOVE;
            ev.originalEvent.dataTransfer.setData(constants.TYPE_TEXT, JSON.stringify(dragData));
            // Un-mark, already marked or not (as opposed to un-select)
            focusController.markControl(ev.currentTarget.id, false);
        });
    }

    unlistenForDrop(target) {
        let t = $(target);
        if (t.length) {
            t.off(constants.EV_DROP + DragDropService.namespace)
                .off(constants.EV_DRAGOVER + DragDropService.namespace);
        }
    }

    initSearchToolBoxDraggable(selected, initialEdges, toolboxItems){

        let self = this;
        let sel = selected;

        $("#toolSearch").on('keypress', function (e) {
            var code = (e.keyCode ? e.keyCode : e.which);
            if (code === 13) { //Enter keycode
                return false;
            }
        });

        let toolSearch = $("#toolSearch");
        if (toolSearch.length) {
                toolSearch.autocomplete({
                    source: this.toolboxItemMatcher(toolboxItems),
                    close: function (event, ui) {
                        if (!sel) {
                            $("ul.ui-autocomplete").hide()
                        }
                    },
                    focus: function (event, ui) {
                        let factoryKey = ui.item.factoryKey;
                        let menuItems = $('li.ui-menu-item div:contains("' + ui.item.value + '")');

                        initialEdges.left = event.pageX;
                        initialEdges.top = event.pageY;
                        initialEdges.right = event.pageX + event.currentTarget.clientWidth;
                        initialEdges.bottom = event.pageY + event.currentTarget.clientHeight;

                        let canvas = $(constants.PAGESEL_ID);
                        let pageRect = {
                            top: -1,
                            left: -1,
                            height: util.extractNum(canvas.height()) + 2,
                            width: util.extractNum(canvas.width()) + 2
                        };

                        _.forEach(menuItems, item => {
                            let modelItem = ui.item;


                            $(item).draggable({
                                containment: "body",
                                scroll: false,
                                helper: function (ev) {
                                    // Reset drag image
                                    focusController.clearAllSelected();
                                    let factories = factoryService.factories;
                                    // Identify the factory whose icon is being dragged
                                    // IE9 must use currentTarget instead of target. Works with others also.
                                    let iconId = modelItem.icon;
                                    //PF-2486 - Form Design - Layout - Cannot Drag Asset onto canvas
                                    const assetId = ev.target.getAttribute("data-asset-id");
                                    const controlType = modelItem.factoryKey;

                                    if (controlType) { //If factory is for an asset then controlType brings us the iconId (Control type)
                                        iconId = controlType;
                                    }
                               
                                    const height = factories[iconId].height;
                                    const width = factories[iconId].width;

                                    const dragImage = self.createRawDragGhost(height, width);
                                    const $ghost = dragImage.$ghost;
                                    // Second: get center point. We drag the center of the drag ghost
                                    let cx = $ghost.width() / 2;
                                    let cy = $ghost.height() / 2;
                                    return $ghost.get(0);
                                },
                                start: function (ev, ui) {

                                    let position = {
                                        left: Math.floor(ui.helper.width() / 2),
                                        top: Math.floor(ui.helper.height() / 2)
                                    };

                                    $(this).draggable('instance').offset.click = position;
                                    self.dropEffect = constants.DROP_CREATE;                              
                                },
                                drag: function (event, ui) {
                                    if (((ui.position.top + ui.helper.height() < (initialEdges.top - (ui.helper.height() / 2))) ||
                                        ((ui.position.top + ui.helper.height()) > initialEdges.bottom - (ui.helper.height() / 2))) ||
                                        (((ui.position.left + ui.helper.width()) < (initialEdges.left - (ui.helper.width() / 2))) ||
                                            (ui.position.left + ui.helper.width()) > (initialEdges.right - (ui.helper.width() / 2)))) {

                                        sel = true;

                                        $("ul.ui-autocomplete").hide();
                                        $("#toolSearch").val('');
                                    }

                                    let elemRect = {
                                        left: Number(ui.position.left - util.pagePos.left),
                                        top: Number(ui.position.top - util.pagePos.top),
                                        width: util.extractNum(ui.helper.width()),
                                        height: util.extractNum(ui.helper.height())
                                    };
                                   
                                    let isInsideCanvas = geometryService.isValidOverlapping(pageRect, elemRect);
                                    let cursor = isInsideCanvas ? "copy" : 'no-drop';

                                    $(ui.helper).css('cursor', cursor);
                                 
                                },
                                stop: function (event, ui) {
                                    
                                    //This was added to avoid the double event if a user dropped a control inside a group, this should only run for aPageSel
                                    try{
                                        
                                        let elemRect = {
                                            left: Number(ui.position.left - util.pagePos.left),
                                            top: Number(ui.position.top - util.pagePos.top),
                                            width: util.extractNum(ui.helper.width()),
                                            height: util.extractNum(ui.helper.height())
                                        };

                                        let isInsideCanvas = geometryService.isValidOverlapping(pageRect, elemRect);
                                        if(!listService.elementWasDropped && isInsideCanvas){
                                            $("#aPageSel").trigger("drop", [ui, event, modelItem.factoryKey]);
                                        }
                                    }
                                    catch(err){
                                        console.log(err);
                                    }
                                    finally{
                                        listService.elementWasDropped = false;
                                    }
                                }
                            });
                        });
                    }
                }).autocomplete().data("ui-autocomplete")._renderItem = function (ul, item) {
                    return $("<li>")
                        .append("<div draggable=true data-factoryKey=" + item.factoryKey + "><span><i style='margin-right:5px' class='fa " + item.icon + "'></i></span>" + item.label + "</div>")
                        .appendTo(ul);
                };
        }

    }

    toolboxItemMatcher(toolboxItems) {
        let self = this; 

        return function findMatches(q, cb) {
            let matches, substringRegex;           

            matches = [];
            let val = $('#toolSearch').val();
            substringRegex = new RegExp(val, 'i');
            $.each(toolboxItems, function (i, toolboxItem) {
                if (substringRegex.test(toolboxItem.name)) {
                    let obj = toolboxItem;
                    obj.label = toolboxItem.name;
                    obj.factoryKey = toolboxItem.factoryKey;
                    if (self.isFeatureEnabledForControl(obj.factoryKey, true)) {
                        matches.push(obj);
                    }
                }
            });
            cb(matches);
        };
    }

    //==========================================================================================
    // When we drop an element over an invalid drop zone this method will fire for keeping our clipboard insted of
    // the data transfer object  
    //==========================================================================================
    listenForDragEnd(target) {

        const namespace = DragDropService.namespace;

        $(target).off(constants.EV_DRAG_END + namespace);

        $(target).on(constants.EV_DRAG_END + namespace, ev => {
            if (window.clipboardData) {
                let data = [];
                // TODO: No defense against data that isn't an array that has eleents. Such can pass through the if above;
                let dataToBeCopied = "";
                if (listService && listService.jsonData) {
                    data = JSON.parse(listService.jsonData);
                    if (data[0].currentCopiedText !== undefined) {
                        dataToBeCopied = data[0].currentCopiedText;
                    }
                }

                window.clipboardData.clearData();
                window.clipboardData.setData("Text", dataToBeCopied);
            }
        });
    }

    dropCreate(ev, data, draggableEvent) {

        let control = this.createcontrol(ev.originalEvent ? ev : draggableEvent, data);
        if (control) {
            // PF-2524: Update parent's minimum width and height based on it's children
            listService.calculateGroupDimensions(0, control.model.get('id'));

            const childList = [];
            const childListView = [];
            control = listService.getObject(control.id);

            const currentChildren = control.model.get('childList');
            if (currentChildren) {
                /*_.forEach(control.model.get('childList'), child => {
                    childList.push(child);
                    childListView.push(listService.getObject(child.get('id')));
                })*/
                childList.push(...currentChildren);
                childListView.push(...currentChildren.map(child => listService.getObject(child.get('id'))));
            }

            // PF-1155 Adding validation to draw a selection rectangle on the timer control.
            if (control.model.get('factoryKey') === 'control_timer') {
                $('.asset-control').removeClass('pfSelected');
                util.jqId(control.model.get('id')).addClass('pfSelected');
            }
            undoService.add({
                undo: () => {
                    if (control.model.get('childList')) {
                        control.model.set('childList', childList, { silent: true });
                        _.forEach(control.model.get('childList'), child => {

                            //PF-3485 Deleting the event raise, as destroy object will do the work.
                            listService.destroyObject(child.id, { silent: false });
                        })
                    }
                    listService.destroyObject(control.id, { silent: false });
                    control.unrender();

                    if(control.input){
                        listService.destroyObject(control.input.id, { silent: false });
                        control.input.unrender();
                    }
                },
                redo: () => {
                    listService.addObject(control.id, control, { pageId: control.model.get('pageId') });
                    control.listenTo(control.model, 'change', control.render);
                    control.render();

                    if(control.input){
                        listService.addObject(control.input.id, control.input, { pageId: control.input.model.get('pageId') });
                        control.input.listenTo(control.input.model, 'change', control.input.render);
                        control.input.render();
                    }

                    // PF-4477 Handling for Radio Button Group and Checkbox Group.
                    if (util.isGroupContainer(control.model.get('factoryKey'))) {
                        control.listenTo(eventBus, 'GroupPropertyChange', control.render);
                        control.listenTo(eventBus, 'GroupThemeChange', control.themeChangeRender);
                    }

                    if (childList) {
                        control.model.set('childList', childList, { silent: true });
                        _.forEach(childListView, child => {
                            child.listenTo(child.model, 'change', child.render);
                            listService.addObject(child.id, child, { pageId: child.model.get('pageId') });
                            child.render();
                        });
                    }
                }
            }, textConstants.UNDO_NEW);

            utilMobile.addedNewObjectToDesktopCanvas(control);
        }

    }

    dropMove(ev, data) {

        const controls = this.moveControl(ev, data);
        //PF-2085. We take the first element to show properties, when
        //it's a multiple selection.
        //when this have just one element.
        if (controls.previous && controls.previous.length === 1 && controls.previous[0].id) {
            let id = controls.previous[0].id;
            let control = listService.getObject(id);
            listService.currentId = id;
            util.updateHelpDocumentationLink(control.model.get('factoryKey'));
            focusController.switchPropsViews();
            $('#settings-tab a').click();
        }

        const self = this;

        // // add undo here to account for Multiple Selection
        undoService.add({
            undo: () => {
                _.forEach(controls.previous, function (c) {
                    self.undoRedoMove(c);
                })
            },
            redo: () => {
                _.forEach(controls.new, function (c) {
                    self.undoRedoMove(c);
                })
            }
        }, textConstants.UNDO_MOVE);
    }

    undoRedoMove(c) {

        const view = listService.getObject(c.id);
         // remove control from group childlist if no longer belonging to group
        if (view.groupId && !c.groupId) {
            crudController.removeChildFromGroupModel(view.groupId, c.id);
        }         
        view.groupId = c.groupId;

        thumbPageListUtil.moveElementToNewParent(view.id, view.groupId, view.pageId);
        this.setNewPosition(view, {top: c.top, left: c.left});
        this.setNewDimensions({height: c.height, width: c.width}, view);
        
        const append = c.groupId ? `${constants.ID_SEL}${'groupItems' + c.groupId}` : `${constants.ID_SEL}aPage`;
        $(constants.ID_SEL + view.id).appendTo(append);
    }

    enableDropInMobile(ev, data) {
        let enableDrop = true;
        const _hostControl = $(ev.target).parents('div.pf-control.group')[0];

        if (_hostControl) {
            enableDrop = !listService.controlHasSection(_hostControl.id);

            if (!enableDrop) {
                let groupType;
                const _control = listService.getObject(_hostControl.id);
                let supportedChildren = (childList, factoryKey) => childList.every(_object => _object.get('factoryKey') === factoryKey);
                if (supportedChildren(_control.model.get('childList'), 'control_checkbox')) {
                    groupType = 'control_checkbox_group';
                } else if (supportedChildren(_control.model.get('childList'), 'control_radio_button')) {
                    groupType = 'control_radio_button_group';
                }

                const cdata = data[0];
                let _draggedCtrlFactoryKey;

                if (cdata.sourceId !== _hostControl.id) {
                    if (cdata.iconId) {
                        _draggedCtrlFactoryKey = cdata.iconId;
                    } else {
                        const _draggedControl = listService.getObject(cdata.sourceId);
                        _draggedCtrlFactoryKey = _draggedControl.model.get('factoryKey');
                    }

                    if (['control_checkbox', 'control_radio_button'].indexOf(_draggedCtrlFactoryKey) !== -1) {
                        enableDrop = (_draggedCtrlFactoryKey === 'control_checkbox' && groupType === 'control_checkbox_group')
                            || (_draggedCtrlFactoryKey === 'control_radio_button' && groupType === 'control_radio_button_group');
                        if (enableDrop) {
                            utilMobile.deleteElementIntoMobileObject(_hostControl.id);
                            utilMobile.addedNewObjectToDesktopCanvas(_control);
                        }
                    }
                }

                let typeOfGroup1, typeOfGroup2;
                if (groupType === 'control_checkbox_group') {
                    typeOfGroup1 = 'checkboxes';
                    typeOfGroup2 = 'Checkbox';
                } else if (groupType === 'control_radio_button_group') {
                    typeOfGroup1 = 'radio buttons';
                    typeOfGroup2 = 'Radio button';
                }
                notificationService.info(`Only ${typeOfGroup1} can be added to ${typeOfGroup2} Groups used in Mobile app design`);
            }
        }

        return enableDrop;
    }

    //==========================================================================================
    // Listens for either kind of drop: creation/add or intra-page move.
    // TODO  Still not delegated, so there are a pair of handlers per control instance.
    //==========================================================================================
    listenForDrop(target) {
        const namespace = DragDropService.namespace;

        //This validation is to avoid the double event
        if (target !== "#aPageSel") {
            $(target).droppable({ greedy: true });
        }

        $(target).off(constants.EV_DROP + namespace).off(constants.EV_DRAGOVER + namespace);

        $(target).on(constants.EV_DROP + namespace, (ev, ui, draggableEvent, fk) => {

            if (ui && ev.currentTarget !== ev.target) {
                return;
            }

            if (ui) {
                listService.elementWasDropped = true;
            }

            ev.originalEvent && ev.originalEvent.preventDefault();
            ev.originalEvent && ev.originalEvent.stopPropagation();

            if (listService.mainKind === constants.KIND_MOBILE) {
                mobileGroupDomHandler.onDropGroupItem(ev);
            } else {
                // Get transferred data. It contains the keys to what to drop where.
                const createDataFormat = (factoryKey, ui) => {
                    return [{ iconId: factoryKey, position: ui.position }];
                };

                const data = ev.originalEvent && ev.originalEvent.dataTransfer ? JSON.parse(ev.originalEvent.dataTransfer.getData(constants.TYPE_TEXT)) :
                    ui.draggable ? createDataFormat(ui.draggable.data().factorykey, ui) : createDataFormat(fk, ui);

                let enableDrop = this.enableDropInMobile(ev, data);

                if (enableDrop) {
                    // Drop factory image vs drop existing instance:
                    if (this.dropEffect === constants.DROP_CREATE) {
                        this.dropCreate(ev, data, draggableEvent);
                    }
                    else if (this.dropEffect === constants.DROP_MOVE) {
                        this.dropMove(ev, data);
                    }

                    // Clearing the JSON data store on the clipboard on IE9 and placing the value that was before the drag started.
                    // TODO: This stanza doesn't work. Bypass: I defended against excpetions by inserting window.clipboardData &&
                    //if (window.clipboardData && typeof ev.originalEvent.dataTransfer.setDragImage !== 'undefined' && typeof window.clipboardData === 'undefined' &&
                    //    (typeof data[0].currentCopiedText !== 'undefined' || typeof data.currentCopiedText !== 'undefined')) {

                    if (window.clipboardData) {
                        // TODO: No defense against data that isn't an array that has eleents. Such can pass through the if above;
                        let dataToBeCopied = "";
                        if (data[0].currentCopiedText !== undefined) {
                            dataToBeCopied = data[0].currentCopiedText;
                        }

                        window.clipboardData.clearData();
                        window.clipboardData.setData("Text", dataToBeCopied);
                    }

                    // Refresh current thumbnail
                    //pageCaptureController.captureThumbnail( );
                }
                return true;
            }
        }).on(constants.EV_DRAGOVER + namespace, (ev, data) => {
            ev.originalEvent.preventDefault();
            // TODO we need to snap the drag image to the grid if listService.snapToGrid is set.
            //console.log(`drag-over`, ev);
            //const p = this.align( ev.originalEvent.pageX, ev.originalEvent.pageY );
            // const pageX = p.x;
            //const pageY = p.y;
            // This sets the drag cursor for browsers that support it. Copy === (+), move === pointer
            return ev.originalEvent.dataTransfer.dropEffect = this.dropEffect;
        });

    }




    checkSnapToNeighbor(ev, cdata, factoryParams) {

        // Distance min and max to detect neighbors.
        const minDistance = 0;
        const maxDistance = 50;
        // Default in case that no snapping is possible.
        let controlPositionAfterSnapping = { left: 0, top: 0 };

        // Getting transfer data or factory view values depending if the control is created or moved.
        let currentData = cdata || {};
        const currentFactoryKey = currentData.iconId || factoryParams.model.attributes.factoryKey;
        const textFactoryKeys = ['control_text', 'control_small_text', 'control_large_heading', 'control_medium_heading', 'control_small_heading'];
        let factoryHeight = factoryParams.height || factoryParams.model.attributes.height;
        let factoryWidth = factoryParams.width || factoryParams.model.attributes.width;

        // Checking if option is enabled or if the current control being dragged is text type.
        if (listService.snapToNeighbor && _.includes(textFactoryKeys, currentFactoryKey)) {
            console.log("Data Transfer", ev.originalEvent.dataTransfer);
            console.log("Factory Params", factoryParams);

            // Getting center X and center Y values at the right side of the dragged control to snap it.
            let draggedControlWidth = parseFloat(factoryWidth);
            let draggedControlHeight = parseFloat(factoryHeight);

            // Page X and Y gives the center coordinates of the control, but we want the edge right size in X and the center of the control in Y.
            let controlDraggedX = ev.originalEvent.pageX + (draggedControlWidth / 2);
            let controlDraggedY = ev.originalEvent.pageY + (draggedControlHeight / 2);

            console.log('Current X', controlDraggedX);
            console.log('Current Y', controlDraggedY);
            let arrayWithLessDistances = [];
            // Iterate controls to see if someone is near from the dropped one.
            let currentControls = listService.getPageControls(listService.currentPageId).toArray();
            let controlsToSnap = currentControls.filter(item => {
                console.log("Item", item);

                // Getting sizes required for calculation of the current control already rendered.
                let currentControlHeight = parseFloat(item.model.attributes.height);
                let currentControlWidth = parseFloat(item.model.attributes.width);
                let controlToSnapX = util.pagePos.left + item.model.attributes.left;
                let controlToSnapY = util.pagePos.top + item.model.attributes.top;

                console.log("ControlToSnapX", controlToSnapX);
                console.log("ControlDraggedX", controlDraggedX);
                // Just for snapping at the left of the control already present in the page.
                if (controlToSnapX < controlDraggedX) {
                    return false;
                }

                console.log("CurrentControl Height", currentControlHeight);
                console.log("CurrentControl Width", currentControlWidth);
                console.log("X:" + (controlDraggedX) + " X1:" + (controlToSnapX));
                console.log("Y:" + (controlDraggedY) + " Y1:" + (controlDraggedY));

                // Calculating distance based on the Phythagorean Distance Formula.
                let distance = Math.sqrt(((Math.pow(2, controlToSnapX - controlDraggedX)) + (Math.pow(2, controlToSnapY - controlDraggedY))));
                console.log("Distance", distance);
                // If its inside of our limits, add it to an array.
                if (distance >= minDistance && distance <= maxDistance) {
                    arrayWithLessDistances.push(distance);
                    return true;
                }
                return false;
            });

            console.log("Controls to Snap", controlsToSnap);

            // If more than two controls were detected, we need to retrieve the one with the less distance.
            // Thats why the array was created before, to retrieve the index of the lowest distance value and filtering into the allControls array already filtered.
            if (controlsToSnap.length > 0) {

                let lowestDistanceIndex = arrayWithLessDistances.reduce((iMin, x, i, arr) => x < arr[iMin] ? i : iMin, 0);

                let controlToSnap = controlsToSnap[lowestDistanceIndex];

                console.log("Final control to Snap", controlToSnap);

                // The new position values are determined here , adding the left and top values according to the neighbor control.
                //if(controlDraggedX <  (util.pagePos.left + parseFloat(controlToSnap.model.attributes.left))){
                controlPositionAfterSnapping = {
                    left: (parseFloat(controlToSnap.model.attributes.left) - parseFloat(factoryWidth)),
                    top: ((parseFloat(controlToSnap.model.attributes.top) +
                        (parseFloat(controlToSnap.model.attributes.height)) / 2)) -
                        (parseFloat(factoryHeight) / 2)
                };

                // TODO in case Snapping on right is required
                // }
                // else{
                //     controlPositionAfterSnapping = {left: parseFloat(controlToSnap.model.attributes.left) + parseFloat(controlToSnap.model.attributes.width),
                //         top: ((parseFloat(controlToSnap.model.attributes.top) + parseFloat(controlToSnap.model.attributes.height)) / 2)
                //         - (parseFloat(factoryParams.height) / 2)}; //+ parseFloat(factoryParams.height)};
                // }
            }
        }
        return controlPositionAfterSnapping;
    }


    //==========================================================================================
    // Currently starts a listener delegate configured for drops of the FACTORY_CLASS.
    // DELEGATEs to factory panel
    //==========================================================================================
    listenForFactoryDragStart(factories) {

        const namespace = DragDropService.namespace;
        // Initializing the value that will be storing the current copied text before the drag begins.
        let currentCopiedText = "";

        $(constants.FACTORIES_ID).off(constants.EV_DRAG_START + namespace, constants.FACTORY_CLASS);

        $(constants.FACTORIES_ID).on(constants.EV_DRAG_START + namespace, constants.FACTORY_CLASS, ev => {
            // Reset drag image
            focusController.clearAllSelected();

            // Identify the factory whose icon is being dragged
            // IE9 must use currentTarget instead of target. Works with others also.
            let iconId = ev.currentTarget.id;
            //PF-2486 - Form Design - Layout - Cannot Drag Asset onto canvas
            const assetId = ev.target.getAttribute("data-asset-id");
            const controlType = ev.target.getAttribute("data-control-type");

            if (controlType) { //If factory is for an asset then controlType brings us the iconId (Control type)
                iconId = controlType;
            }
            const height = factories[iconId].height;
            const width = factories[iconId].width;

            const dragImage = this.createRawDragGhost(height, width, factories[iconId].hasLabel);
            const $ghost = dragImage.$ghost;
            // Second: get center point. We drag the center of the drag ghost
            let cx = $ghost.width() / 2;
            let cy = $ghost.height() / 2;

            

            // If dataTransfer.setDragImage is not implemented, then is a browser without native support for custom drag image, mostly Internet Explorer
            if (ev.originalEvent.dataTransfer.setDragImage) {
                let devicePixelRatio = util.getBrowserZoom();

                let offSetX = cx;
                let offSetY = cy;

                if(devicePixelRatio > 1){
                    devicePixelRatio = devicePixelRatio + (devicePixelRatio / 2);
                    offSetX = cx * devicePixelRatio;
                    offSetY = cy * devicePixelRatio;
                }

                ev.originalEvent.dataTransfer.setDragImage($ghost.get(0), offSetX, offSetY);
                
                
            } 
            const dataJson = JSON.stringify([{
                iconId: iconId,
                cx: cx,
                cy: cy,
                height: $ghost.height(),
                width: $ghost.width(),
                assetId: assetId,
                currentCopiedText: currentCopiedText
            }]);

            // Stateful singleton hack for IE9; still satisfies higher browsers
            this.dropEffect = constants.DROP_CREATE;
            ev.originalEvent.dataTransfer.setData(constants.TYPE_TEXT, dataJson);
            listService.jsonData = dataJson;

        });
    }

    //==========================================================================================
    // Used for behaviors object drops.
    // Listens for either kind of drop: creation/add or intra-page move.
    // TODO  Still not delegated, so there are a pair of handlers per control instance.
    //==========================================================================================
    listenForDropBehaviors(target) {

        const namespace = DragDropService.namespaceBehaviors;

        $(target).off(constants.EV_DROP + namespace).off(constants.EV_DRAGOVER + namespace);

        $(target).on(constants.EV_DROP + namespace, ev => {
            ev.originalEvent.preventDefault();

            // Get transferred data. It contains the keys to what to drop where
            const data = JSON.parse(ev.originalEvent.dataTransfer.getData(constants.TYPE_TEXT));

            // Drop factory image vs drop existing instance:
            if (this.dropEffect === constants.DROP_CREATE) {
                this.createControlBehaviors(ev, data);
                eventBus.trigger(constants.EV_DELETE_TREE_FILTER);
            }
            $('#settings-tab a').trigger("click");

            eventBus.trigger(constants.EV_REFRESH_REMOTE_DATA_FILTER);

            return true;
        }).on(constants.EV_DRAGOVER + namespace, ev => {
            ev.originalEvent.preventDefault();


            // This sets the drag cursor for browsers that support it. Copy === (+), move === pointer
            return ev.originalEvent.dataTransfer.dropEffect = this.dropEffect;
        });
    }


    //==========================================================================================
    // Currently starts a listener delegate configured for drops of the FACTORY_CLASS.
    // Use this method to start factory drags for behaviors or workflow.
    // This method sets no custom drag ghost.
    // DELEGATEs to factory panel
    //==========================================================================================
    listenForFactoryDragStartBehaviors(factories) {

        const namespace = DragDropService.namespaceBehaviors;
        let currentCopiedText = "";

        $(constants.FACTORIES_ID).off(constants.EV_DRAG_START + namespace, constants.FACTORY_CLASS);

        $(constants.FACTORIES_ID).on(constants.EV_DRAG_START + namespace, constants.FACTORY_CLASS, ev => {

            // Reset drag image
            focusController.clearAllSelected();

            // Identify the factory whose icon is being dragged
            // IE9 must use currentTarget instead of target. Works with others also.
            const iconId = ev.currentTarget.id;
            const height = factories[iconId].height;
            const width = factories[iconId].width;

            const cx = width / 2;
            const cy = height / 2;

            const dataJson = JSON.stringify([{
                iconId: iconId,
                cx: cx,
                cy: cy,
                height: height,
                width: width,
                currentCopiedText: currentCopiedText
            }]);

            // Stateful singleton hack for IE9; still satisfies higher browsers
            this.dropEffect = constants.DROP_CREATE;
            ev.originalEvent.dataTransfer.setData(constants.TYPE_TEXT, dataJson);
            listService.jsonData = dataJson;
            console.log(`Behaviors drag start iconID: '${iconId}'`);

        });
    }

    listenForDropWorkflow(target) {

        const namespace = DragDropService.namespaceWorkflow;

        $(target).off(constants.EV_DROP + namespace).off(constants.EV_DRAGOVER + namespace);

        $(target).on(constants.EV_DROP + namespace, ev => {
            ev.originalEvent.preventDefault();

            // Get transferred data. It contains the keys to what to drop where
            const data = JSON.parse(ev.originalEvent.dataTransfer.getData(constants.TYPE_TEXT));

            // Drop factory image vs drop existing instance:
            if (this.dropEffect === constants.DROP_CREATE) {
                this.createControlWorkflow(ev, data);
                eventBus.trigger(constants.EV_DELETE_TREE_FILTER);
            }
            else if (this.dropEffect === constants.DROP_MOVE) {
                this.moveControlWorkflow(ev, data);
            }
            return true;
        }).on(constants.EV_DRAGOVER + namespace, ev => {
            ev.originalEvent.preventDefault();

            // This sets the drag cursor for browsers that support it. Copy === (+), move === pointer
            return ev.originalEvent.dataTransfer.dropEffect = this.dropEffect;
        });
    }


    //==========================================================================================
    // Currently starts a listener delegate configured for drops of the FACTORY_CLASS.
    // Use this method to start factory drags for behaviors or workflow.
    // This method sets no custom drag ghost.
    // DELEGATEs to factory panel
    //==========================================================================================
    listenForFactoryDragStartWorkflow(factories) {

        const namespace = DragDropService.namespaceWorkflow;
        let currentCopiedText = "";

        $(constants.FACTORIES_ID).on(constants.EV_DRAG_START + namespace, constants.FACTORY_CLASS, ev => {

            // Reset drag image
            focusController.clearAllSelected();

            // Identify the factory whose icon is being dragged
            // IE9 must use currentTarget instead of target. Works with others also.
            const iconId = ev.currentTarget.id;
            const height = factories[iconId].height;
            const width = factories[iconId].width;

            const cx = width / 2;
            const cy = height / 2;

            const dataJson = JSON.stringify([{
                iconId: iconId,
                cx: cx,
                cy: cy,
                height: height,
                width: width,
                currentCopiedText: currentCopiedText
            }]);

            // Stateful singleton hack for IE9; still satisfies higher browsers
            this.dropEffect = constants.DROP_CREATE;
            ev.originalEvent.dataTransfer.setData(constants.TYPE_TEXT, dataJson);
            listService.jsonData = dataJson;
            console.log(`Workflow drag start iconID: '${iconId}'`);

        });
    }

}


//==========================================================================================
// Export the instance, not the class. A singleton instance.
//==========================================================================================
const dragDropService = new DragDropService();
export default dragDropService;
